import { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames/bind';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { consignmentSlice } from '@api/Trinium/Endpoints/Consignment/slice';
import { LegType } from '@api/Trinium/types';
import RenderCell from '../../RenderCell';
import { CellProps } from '../../types';
import { setModalSearchParameters } from '@hooks/useModalParams';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppSelector } from '@reduxStore';
import { GridRenderCellParams, gridRowIdSelector, useGridApiContext } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

const cx = classNames.bind(styles);

/**
 * Component for rendering the Order Number column Grid Cells
 */
export default function OrderNumber(props: CellProps<DispatchLeg> & Pick<GridRenderCellParams, 'tabIndex'>) {
  const gridApi = useGridApiContext();
  const { jobId, jobCategoryTransportationType, csnNo, legId, legNo, legType, csnId } = props.row;
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(legId));

  const fullOrderNumber = useMemo(() => `${jobId.toString()}/${csnNo.toString().padStart(3, '0')}`, [jobId, csnNo]);

  // A load with no leg (cancelled, closed/purged, or deleted) has no leg number/type to display.
  const hasNoLeg = !legId;
  const legDisplay = useMemo(() => (hasNoLeg ? '' : `${String(legNo)}${legType ? ` - ${legType}` : ''}`), [hasNoLeg, legNo, legType]);

  /** Warm the consignment cache and the modal's lazy chunk once the pointer settles on the link so the modal opens with its data already there.
   *  The short delay keeps a mouse sweeping down the column from firing a request per row. */
  const { number: terminalNumber } = useCurrentTerminal();
  const prefetchConsignment = consignmentSlice.usePrefetch('fetchConsignment', { ifOlderThan: 10 });
  const hoverTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    return () => {
      window.clearTimeout(hoverTimer.current);
    };
  }, []);

  const handleMouseEnter = () => {
    hoverTimer.current = window.setTimeout(() => {
      prefetchConsignment({ terminalNumber, consignmentId: csnId });
      void import('@/ConsignmentDetails/ConsignmentFormikModal');
    }, 150);
  };

  const handleMouseLeave = () => {
    window.clearTimeout(hoverTimer.current);
  };

  const handleBtnClick = () => {
    if (isLoading) {
      return;
    }
    gridApi.current.setRowSelectionModel({
      type: 'include',
      ids: new Set([gridRowIdSelector(gridApi, props.row)])
    });
    /** Opening the modal re-renders everything subscribed to the search params, which can hold the paint for a noticeable moment.
     *  Waiting until after the next frame lets the row selection paint first, so the click visibly registers right away. */
    requestAnimationFrame(() => {
      setTimeout(() => {
        setModalSearchParameters({
          form: 'consignmentDetails',
          formId: String(csnId),
          legId: hasNoLeg ? '0' : String(legId)
        });
      });
    });
  };

  return (
    <RenderCell
      primarySlot={
        // tabIndex follows the grid's roving focus (0 only on the focused cell) so Tab doesn't walk every row
        <div
          className={cx('row-link', { disabled: isLoading })}
          title={fullOrderNumber}
          role='button'
          tabIndex={props.tabIndex}
          aria-label={`Open order ${fullOrderNumber}`}
          aria-disabled={isLoading}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleBtnClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              // Keeps the grid from also treating the key as cell navigation/selection
              event.preventDefault();
              event.stopPropagation();
              handleBtnClick();
            }
          }}>
          <span className={styles.bold}>{jobId}</span>
          <span className={styles['secondary-text']}>/{csnNo.toString().padStart(3, '0')}</span>
        </div>
      }
      secondarySlot={
        <span
          className={styles.bold}
          title={jobCategoryTransportationType}>
          {jobCategoryTransportationType.toUpperCase()}
        </span>
      }
      tertiarySlot={
        <span
          className={styles.bold}
          title={hasNoLeg ? '' : `Leg ${String(legNo)} - ${getEnumValueByKey(LegType, legType) ?? ''}`}>
          {legDisplay}
        </span>
      }
      disableIndentation
    />
  );
}
